import { ReactNode, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import useAIAgentPin from '../hooks/useAIAgentPin'

interface AIAgentPinGateProps {
  children: ReactNode
  lockedFallback?: ReactNode
  bypass?: boolean
  onCancel?: () => void
}

export default function AIAgentPinGate({
  children,
  lockedFallback,
  bypass = false,
  onCancel
}: AIAgentPinGateProps) {
  const [t] = useTranslation('common')
  const { isPinUnlocked, isUnlocking, unlockPin } = useAIAgentPin()
  const [pinCode, setPinCode] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const isAccessible = bypass || isPinUnlocked

  const handleUnlock = async () => {
    if (isUnlocking || !pinCode.trim()) {
      return
    }

    setPinError(null)

    try {
      if (!(await unlockPin(pinCode))) {
        setPinError(t('aiAgentPinInvalid', { defaultValue: 'Invalid PIN code.' }))
        return
      }

      setPinCode('')
    } catch {
      setPinError(
        t('aiAgentPinVerificationFailed', {
          defaultValue: 'Could not verify the PIN code. Please try again.'
        })
      )
    }
  }

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0 }}>
      {isAccessible
        ? children
        : (lockedFallback ?? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 320,
                p: { xs: 2, md: 3 }
              }}
            >
              <Typography color="text.secondary">
                {t('aiAgentPinLockedHint', {
                  defaultValue: 'Enter the PIN code to open the AI agent.'
                })}
              </Typography>
            </Box>
          ))}
      <Dialog
        open={!isAccessible}
        onClose={onCancel}
        disableEscapeKeyDown={!onCancel}
        fullWidth
        maxWidth="xs"
        aria-labelledby="ai-agent-pin-title"
        aria-describedby="ai-agent-pin-description"
      >
        <DialogTitle id="ai-agent-pin-title">
          {t('aiAgentPinDialogTitle', { defaultValue: 'AI Agent PIN code' })}
        </DialogTitle>
        <DialogContent>
          <Typography id="ai-agent-pin-description" variant="body2" color="text.secondary">
            {t('aiAgentPinDialogDescription', {
              defaultValue: 'Enter the PIN code to continue to the AI agent.'
            })}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            value={pinCode}
            disabled={isUnlocking}
            onChange={(event) => {
              setPinCode(event.target.value)
              setPinError(null)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault()
                void handleUnlock()
              }
            }}
            label={t('aiAgentPinFieldLabel', { defaultValue: 'PIN code' })}
            inputProps={{ maxLength: 32 }}
            sx={{ mt: 2.5 }}
          />
          {pinError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {pinError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          {onCancel && <Button onClick={onCancel}>{t('cancel')}</Button>}
          <Button
            onClick={() => void handleUnlock()}
            variant="contained"
            disabled={isUnlocking || pinCode.trim().length === 0}
            startIcon={isUnlocking ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('aiAgentPinSubmit', { defaultValue: 'Open AI agent' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
